import { useState } from "react";
import { Image as ImageIcon, Video, FileText, ExternalLink, Figma, BookOpen, Music, Lock, X, Plus, Upload, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PIECE_TYPES, WORK_TYPES, WORK_TYPE_LABEL, fileUrl, videoEmbedUrl, figmaEmbedUrl, normalizePiece } from "@/lib/portfolio";

const ICONS = {
  image: ImageIcon, video: Video, pdf: FileText, url: ExternalLink,
  figma: Figma, case_study: BookOpen, audio: Music, on_request: Lock,
};

function newPiece(type) {
  return normalizePiece({
    id: crypto.randomUUID(),
    title: "",
    description: "",
    work_type: "other",
    visibility: "public",
    piece_type: type,
    image_paths: [],
    case_study: type === "case_study"
      ? { problem: { text: "", image_paths: [] }, process: { text: "", image_paths: [] }, outcome: { text: "", image_paths: [] } }
      : null,
  });
}

async function uploadFile(endpoint, file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data.storage_path;
}

export default function PortfolioPieceEditor({ pieces, onChange, dataTestidPrefix = "piece" }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);

  const update = (idx, patch) => {
    const a = [...pieces];
    a[idx] = normalizePiece({ ...a[idx], ...patch });
    onChange(a);
  };
  const remove = (idx) => onChange(pieces.filter((_, i) => i !== idx));
  const add = (type) => {
    const np = newPiece(type);
    onChange([...pieces, np]);
    setPickerOpen(false);
    setEditingIdx(pieces.length);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {pieces.map((rawP, i) => {
          const p = normalizePiece(rawP);
          const Icon = ICONS[p.piece_type] || ImageIcon;
          const expanded = editingIdx === i;
          return (
            <div key={p.id || i} className="panel p-5 relative" data-testid={`${dataTestidPrefix}-${i}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5} />
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground truncate">
                    {PIECE_TYPES.find((t) => t.type === p.piece_type)?.label || p.piece_type}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditingIdx(expanded ? null : i)} className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-primary" data-testid={`${dataTestidPrefix}-${i}-toggle`}>
                    {expanded ? "Collapse" : "Edit"}
                  </button>
                  <button onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive" data-testid={`${dataTestidPrefix}-${i}-remove`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <input
                className="input-base mb-3"
                placeholder="Title"
                value={p.title}
                onChange={(e) => update(i, { title: e.target.value })}
                data-testid={`${dataTestidPrefix}-${i}-title`}
              />
              <textarea
                className="input-base mb-3 min-h-[60px]"
                placeholder="Short description (2-3 sentences)"
                value={p.description}
                onChange={(e) => update(i, { description: e.target.value })}
                data-testid={`${dataTestidPrefix}-${i}-description`}
              />
              <div className="grid grid-cols-2 gap-2 mb-3">
                <select
                  className="input-base"
                  value={p.work_type || "other"}
                  onChange={(e) => update(i, { work_type: e.target.value })}
                  data-testid={`${dataTestidPrefix}-${i}-work-type`}
                >
                  {WORK_TYPES.map((t) => <option key={t} value={t}>{WORK_TYPE_LABEL[t] || t}</option>)}
                </select>
                <label className="flex items-center justify-between input-base cursor-pointer text-xs" data-testid={`${dataTestidPrefix}-${i}-visibility`}>
                  <span className="text-muted-foreground">{p.visibility === "public" ? "Public" : "On request"}</span>
                  <input
                    type="checkbox"
                    checked={p.visibility === "public"}
                    onChange={(e) => update(i, { visibility: e.target.checked ? "public" : "on_request" })}
                    className="ml-2"
                  />
                </label>
              </div>

              {expanded && <TypeSpecificFields piece={p} onChange={(patch) => update(i, patch)} testidPrefix={`${dataTestidPrefix}-${i}`} />}

              {(p.signals?.tools_used?.length || p.signals?.verticals?.length || p.signals?.outputs?.length || p.signals?.roles?.length) ? (
                <SignalChips signals={p.signals} testidPrefix={`${dataTestidPrefix}-${i}`} />
              ) : (p.title || p.description) ? (
                <div className="mt-3 text-[10px] tracking-[0.18em] uppercase text-muted-foreground/70 font-mono inline-flex items-center gap-2" data-testid={`${dataTestidPrefix}-${i}-signals-pending`}>
                  <Loader2 className="w-3 h-3 animate-spin" /> Analyzing piece on save…
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {pickerOpen ? (
        <div className="panel p-5" data-testid={`${dataTestidPrefix}-picker`}>
          <div className="flex items-center justify-between mb-4">
            <div className="terminal-label text-primary">[ CHOOSE.TYPE ]</div>
            <button onClick={() => setPickerOpen(false)} className="text-muted-foreground hover:text-foreground text-[10px] uppercase tracking-[0.18em]">Cancel</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {PIECE_TYPES.map((t) => {
              const Icon = ICONS[t.type];
              return (
                <button
                  key={t.type}
                  onClick={() => add(t.type)}
                  data-testid={`${dataTestidPrefix}-picker-${t.type}`}
                  className="text-left panel p-4 hover:border-primary transition-colors group"
                >
                  <Icon className="w-5 h-5 text-foreground/70 group-hover:text-primary mb-3" strokeWidth={1.5} />
                  <div className="serif-mod text-base mb-1 tracking-tight" style={{ fontWeight: 500 }}>{t.label}</div>
                  <div className="text-xs text-muted-foreground leading-relaxed">{t.helper}</div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <button
          onClick={() => setPickerOpen(true)}
          data-testid={`${dataTestidPrefix}-add`}
          className="btn-secondary inline-flex items-center gap-2"
        >
          <Plus className="w-3 h-3" /> Add piece
        </button>
      )}
    </div>
  );
}

function TypeSpecificFields({ piece, onChange, testidPrefix }) {
  switch (piece.piece_type) {
    case "image":      return <ImageFields piece={piece} onChange={onChange} testidPrefix={testidPrefix} />;
    case "video":      return <VideoFields piece={piece} onChange={onChange} testidPrefix={testidPrefix} />;
    case "pdf":        return <PdfFields piece={piece} onChange={onChange} testidPrefix={testidPrefix} />;
    case "url":        return <UrlFields piece={piece} onChange={onChange} testidPrefix={testidPrefix} />;
    case "figma":      return <FigmaFields piece={piece} onChange={onChange} testidPrefix={testidPrefix} />;
    case "audio":      return <AudioFields piece={piece} onChange={onChange} testidPrefix={testidPrefix} />;
    case "case_study": return <CaseStudyFields piece={piece} onChange={onChange} testidPrefix={testidPrefix} />;
    case "on_request": return <OnRequestNote testidPrefix={testidPrefix} />;
    default: return null;
  }
}

function ImageUploader({ paths, multiple = false, onChange, label = "image", endpoint = "/upload/portfolio-image", testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const results = await Promise.all(files.map((f) => uploadFile(endpoint, f)));
      onChange(multiple ? [...(paths || []), ...results] : [results[0]]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <label className="block">
        <input type="file" accept="image/*" multiple={multiple} className="hidden" onChange={handle} data-testid={testid} />
        <div className={`border border-dashed p-3 text-center text-xs uppercase tracking-[0.18em] cursor-pointer hover:border-primary ${(paths || []).length ? "border-accent-green text-accent-green" : "border-border text-muted-foreground"}`}>
          {busy ? <span className="inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> uploading…</span>
            : (paths || []).length ? `✓ ${(paths || []).length} ${label}${(paths || []).length === 1 ? "" : "s"} uploaded - add more` : `Upload ${label}${multiple ? "s" : ""}`}
        </div>
      </label>
      {(paths || []).length > 0 && (
        <div className="mt-2 grid grid-cols-4 gap-2">
          {paths.map((path, i) => (
            <div key={path} className="relative aspect-square bg-surface group">
              <img src={fileUrl(path)} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => onChange(paths.filter((_, j) => j !== i))}
                className="absolute top-1 right-1 bg-background/80 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`${testid}-remove-${i}`}
              >
                <X className="w-3 h-3 text-destructive" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ImageFields({ piece, onChange, testidPrefix }) {
  return (
    <ImageUploader
      paths={piece.image_paths || []}
      multiple
      onChange={(image_paths) => onChange({ image_paths, image_path: image_paths[0] || null })}
      testid={`${testidPrefix}-image-upload`}
    />
  );
}

function VideoFields({ piece, onChange, testidPrefix }) {
  const embed = videoEmbedUrl(piece.video_url);
  return (
    <div>
      <input
        className="input-base"
        placeholder="https://youtu.be/… or https://vimeo.com/…"
        value={piece.video_url || ""}
        onChange={(e) => onChange({ video_url: e.target.value })}
        data-testid={`${testidPrefix}-video-url`}
      />
      {piece.video_url && (
        embed
          ? <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-accent-green">✓ Recognized - will embed on portfolio</p>
          : <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-accent-amber">Unrecognized URL - only YouTube and Vimeo embed</p>
      )}
    </div>
  );
}

function PdfFields({ piece, onChange, testidPrefix }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const path = await uploadFile("/upload/portfolio-pdf", file);
      onChange({ pdf_path: path });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="block">
      <input type="file" accept="application/pdf" className="hidden" onChange={handle} data-testid={`${testidPrefix}-pdf-upload`} />
      <div className={`border border-dashed p-3 text-center text-xs uppercase tracking-[0.18em] cursor-pointer hover:border-primary ${piece.pdf_path ? "border-accent-green text-accent-green" : "border-border text-muted-foreground"}`}>
        {busy ? <span className="inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> uploading…</span>
          : piece.pdf_path ? "✓ PDF uploaded" : "Upload PDF"}
      </div>
    </label>
  );
}

function UrlFields({ piece, onChange, testidPrefix }) {
  return (
    <input
      className="input-base"
      placeholder="https://example.com"
      value={piece.external_url || ""}
      onChange={(e) => onChange({ external_url: e.target.value })}
      data-testid={`${testidPrefix}-external-url`}
    />
  );
}

function FigmaFields({ piece, onChange, testidPrefix }) {
  const embed = figmaEmbedUrl(piece.figma_url);
  return (
    <div>
      <input
        className="input-base"
        placeholder="https://www.figma.com/file/…"
        value={piece.figma_url || ""}
        onChange={(e) => onChange({ figma_url: e.target.value })}
        data-testid={`${testidPrefix}-figma-url`}
      />
      {piece.figma_url && !embed && (
        <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-accent-amber">Doesn&apos;t look like a figma.com link.</p>
      )}
    </div>
  );
}

function AudioFields({ piece, onChange, testidPrefix }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const path = await uploadFile("/upload/portfolio-audio", file);
      onChange({ audio_path: path });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="block">
      <input type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg" className="hidden" onChange={handle} data-testid={`${testidPrefix}-audio-upload`} />
      <div className={`border border-dashed p-3 text-center text-xs uppercase tracking-[0.18em] cursor-pointer hover:border-primary ${piece.audio_path ? "border-accent-green text-accent-green" : "border-border text-muted-foreground"}`}>
        {busy ? <span className="inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> uploading…</span>
          : piece.audio_path ? "✓ Audio uploaded" : "Upload audio (MP3 / WAV / M4A / OGG)"}
      </div>
    </label>
  );
}

function CaseStudyFields({ piece, onChange, testidPrefix }) {
  const cs = piece.case_study || {
    problem: { text: "", image_paths: [] }, process: { text: "", image_paths: [] }, outcome: { text: "", image_paths: [] },
  };
  const updateSection = (section, patch) => {
    onChange({ case_study: { ...cs, [section]: { ...cs[section], ...patch } } });
  };
  return (
    <div className="space-y-4">
      {[
        { key: "problem", label: "The problem" },
        { key: "process", label: "The process" },
        { key: "outcome", label: "The outcome" },
      ].map(({ key, label }) => (
        <div key={key} className="border-l-2 border-primary/40 pl-3">
          <div className="terminal-label text-primary mb-2">{label}</div>
          <textarea
            className="input-base mb-2 min-h-[60px]"
            placeholder={`Describe ${label.toLowerCase()}…`}
            value={cs[key]?.text || ""}
            onChange={(e) => updateSection(key, { text: e.target.value })}
            data-testid={`${testidPrefix}-cs-${key}-text`}
          />
          <ImageUploader
            paths={cs[key]?.image_paths || []}
            multiple
            onChange={(image_paths) => updateSection(key, { image_paths })}
            label="image"
            testid={`${testidPrefix}-cs-${key}-images`}
          />
        </div>
      ))}
    </div>
  );
}

function OnRequestNote({ testidPrefix }) {
  return (
    <div className="border border-dashed border-foreground/20 p-4 text-xs text-muted-foreground" data-testid={`${testidPrefix}-on-request-note`}>
      <Lock className="w-3 h-3 inline mr-2 text-primary" />
      No file required. The piece will appear on your portfolio with an &ldquo;Available on request&rdquo; badge.
    </div>
  );
}

function SignalChips({ signals, testidPrefix }) {
  const groups = [
    { key: "tools_used", label: "Tools", items: signals?.tools_used || [] },
    { key: "verticals",  label: "Verticals", items: signals?.verticals || [] },
    { key: "outputs",    label: "Outputs",   items: signals?.outputs || [] },
    { key: "roles",      label: "Role",      items: signals?.roles || [] },
  ].filter((g) => g.items.length > 0);
  if (groups.length === 0) return null;
  return (
    <div className="mt-4 pt-4 border-t border-foreground/10 space-y-2" data-testid={`${testidPrefix}-signals`}>
      <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground font-mono inline-flex items-center gap-2">
        <Sparkles className="w-3 h-3 text-primary" /> AI signals
      </div>
      <div className="space-y-1.5">
        {groups.map((g) => (
          <div key={g.key} className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[9px] tracking-[0.22em] uppercase text-foreground/45 font-mono w-16 shrink-0">{g.label}</span>
            {g.items.map((it) => (
              <span key={it} className="text-[10px] font-mono px-2 py-0.5 border border-foreground/20 text-foreground/75" data-testid={`${testidPrefix}-signal-${g.key}`}>
                {it}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
